// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Grid, Typography } from '@mui/material'

// ** Types
import {
  OverviewWidgetFilterOptionsType,
  ConversionScaleUnitType,
  ConversionUnitType,
  PaymentMethodType,
  WidgetType
} from 'src/lib/types/dashboardAnalytics'

// ** Child Imports
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import ChartComponent from '../visualizations/ChartComponent'

// ** Helpers
import { conversionScaleOptions, conversionUnitOptions, paymentMethodOptions } from 'src/assets/AnalyticsConfig'
import { analyticsConstants } from 'src/lib/utils/constants'
import {
  extractDateFromFilters,
  getAnalyticsWidgetPayload,
  getAvailableTimeScales,
  getGradient,
  getTimeUnit,
  mapPaymentMode
} from 'src/lib/utils/analyticsHelpers'
import { appliedDateFilter } from 'src/lib/utils/helpers'

// ** Context
import { useFilter } from 'src/lib/contexts/FilterContext'

const SuccessRateChart: React.FC<WidgetType> = ({ value, isLoading, triggerRefresh, customFilters }) => {
  const { analyticsFilters } = useFilter()

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType>('UPI')
  const [selectedConversionUnit, setSelectedConversionUnit] = useState<ConversionUnitType>('percentage')
  const [selectedScaleUnit, setSelectedScaleUnit] = useState<ConversionScaleUnitType>('daily')
  const [availableConversionScaleOptions, setAvailableConversionScaleOptions] =
    useState<OverviewWidgetFilterOptionsType[]>(conversionScaleOptions)

  const plotData = value?.find((plotArray: any) => plotArray?.dimension?.includes(selectedConversionUnit))?.data

  const dataSets = {
    datasets: [
      {
        label: mapPaymentMode(selectedPaymentMethod),
        pointHitRadius: 20,
        pointRadius: 4,
        data: plotData,
        borderColor: function (context: any) {
          const chart = context.chart
          const { ctx, chartArea } = chart

          if (!chartArea) {
            return
          }
          return getGradient(ctx, chartArea, '#01CCA7')
        },
        backgroundColor: 'rgb(255, 255, 255)',
        cubicInterpolationMode: 'monotone'
      }
    ]
  }

  useEffect(() => {
    if (typeof selectedPaymentMethod !== 'undefined' && typeof selectedScaleUnit !== 'undefined') {
      triggerRefresh(
        getAnalyticsWidgetPayload(
          analyticsConstants.FLO_SUCCESS_RATE,
          extractDateFromFilters(analyticsFilters, 'startDate'),
          extractDateFromFilters(analyticsFilters, 'endDate'),
          selectedScaleUnit,
          selectedPaymentMethod,
          customFilters
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod, selectedScaleUnit])

  useEffect(() => {
    setSelectedPaymentMethod('UPI')
    setSelectedScaleUnit('daily')
    setAvailableConversionScaleOptions(getAvailableTimeScales(analyticsFilters))
  }, [analyticsFilters])

  const handleMethodsDropdownClick = (index: number) => {
    setSelectedPaymentMethod(paymentMethodOptions?.[index]?.value as PaymentMethodType)
  }

  const handleScaleDropdownClick = (index: number) => {
    setSelectedScaleUnit(availableConversionScaleOptions[index].value as ConversionScaleUnitType)
  }

  return (
    <>
      <Grid container width={'100%'} gap={4} position={'relative'}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}
        >
          <DropdownButton
            options={paymentMethodOptions}
            handleClick={handleMethodsDropdownClick}
            variant='outlined'
            selectedOption={paymentMethodOptions?.findIndex(
              (option: OverviewWidgetFilterOptionsType, index: number) => option?.value === selectedPaymentMethod
            )}
          />
          <DropdownButton
            options={availableConversionScaleOptions}
            handleClick={handleScaleDropdownClick}
            variant='outlined'
            selectedOption={availableConversionScaleOptions?.findIndex(
              (option: OverviewWidgetFilterOptionsType, index: number) => option?.value === selectedScaleUnit
            )}
          />
        </div>
        <div
          style={{
            padding: '0rem',
            width: '100%',
            position: 'relative',
            minHeight: '180px',
            display: 'flex',
            gap: 8,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 20
          }}
        >
          <div style={{ height: '340px', maxHeight: 'fit-content', width: '100%' }}>
            <ChartComponent
              chartType={'trend_chart'}
              data={dataSets}
              selectedConversionUnit={selectedConversionUnit}
              isLoading={isLoading}
              scaleUnit={getTimeUnit(selectedScaleUnit)}
            />
          </div>
          <div className='flex justify-end w-full'>
            <span className='px-2 py-1.5 text-xs font-semibold rounded bg-gray-lighter text-end'>
              {appliedDateFilter(analyticsFilters)}
            </span>
          </div>
        </div>
      </Grid>
    </>
  )
}

export default SuccessRateChart
